import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { fmtDate } from '../../../lib/scheduleFormat';

// Preview before anything breaks: shrinking availability, or editing or
// cancelling a booking, first names exactly what it would invalidate. On
// confirmation the affected records are flagged as conflicts — nothing
// downstream is deleted automatically.
const ImpactPreviewDialog = ({ open, impact, busy, title, confirmLabel, onConfirm, onCancel }) => {
  const totals = impact?.totals || { dates: 0, bookings: 0, shifts: 0 };
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <AlertDialogContent className="max-w-2xl" data-testid="impact-preview-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-gray-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            {title || 'This change breaks existing plans'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-gray-600">
              <p>
                {totals.bookings} booking{totals.bookings === 1 ? '' : 's'} and {totals.shifts}{' '}
                shift{totals.shifts === 1 ? '' : 's'} across {totals.dates} day
                {totals.dates === 1 ? '' : 's'} would no longer fit. Nothing is deleted — each one is
                flagged for you to resolve.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-[50vh] overflow-y-auto space-y-4 pr-1" data-testid="impact-preview-list">
          {(impact?.affected_dates || []).map((day) => (
            <div key={day.date} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
              data-testid={`impact-day-${day.date}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold text-gray-900">{fmtDate(day.date)}</p>
                <p className="text-xs text-gray-600">
                  {day.current_windows} → <span className="font-medium">{day.proposed_windows}</span>
                </p>
              </div>

              {day.bookings?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {day.bookings.map((b) => (
                    <li key={b.id} className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="bg-white">Booking</Badge>
                      <span>{b.window} · {b.hours}h</span>
                      {b.serves_delivery_dates?.length > 0 && (
                        <span className="text-xs text-gray-600">
                          serves {b.serves_delivery_dates.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {day.shifts?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {day.shifts.map((s) => (
                    <li key={s.id} className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="bg-white capitalize">{s.position_code}</Badge>
                      <span className="font-medium">{s.user_name}</span>
                      <span>{s.window}</span>
                      <span className="text-xs text-gray-600">— {s.reason}</span>
                    </li>
                  ))}
                </ul>
              )}

              {day.delivery_dates?.length > 0 && (
                <p className="mt-2 text-xs text-gray-700">
                  Delivery dates affected: <span className="font-medium">{day.delivery_dates.join(', ')}</span>
                </p>
              )}
            </div>
          ))}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="impact-preview-cancel" disabled={busy}>Go back</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="impact-preview-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {confirmLabel || 'Save anyway'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ImpactPreviewDialog;
